import {
  Button,
  ButtonColor,
  ButtonSize,
  Modal,
  ModalType,
  Option,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { DocumentUpload } from 'src/front/api/apiInstance';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';

import {
  Container,
  FormRow,
  InfoLabel,
  InfoRow,
  Subtitle,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';

import {
  useClearDispatch,
  useCustomerSearchState,
  useSetDocumentsDispatch,
} from 'src/front/contexts/UploadDocumentContext';
import { Separator } from 'src/front/pages/applications/card-observation/CardObservationActivation';

import UploadDocument from './components/UploadDocument';

interface Props {
  goToPreviousStep: () => void;
  setIsCompleted?: (value: boolean) => void;
}

const UploadDocumentList: FC<Props> = ({ goToPreviousStep, setIsCompleted }) => {
  const { clientInfo, documents, customerSearchValues } = useCustomerSearchState();

  const { t } = useTranslation();

  const dispatch = useSetDocumentsDispatch();
  const clearSearchInputs = useClearDispatch();

  const [documentTypes, setDocumentTypes] = useState<Option[]>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasNoDocumentWarning, setHasNoDocumentWarning] = useState<boolean>(false);

  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);

  const loadDocumentTypes = useCallback(async (): Promise<void> => {
    try {
      const { data } = await DocumentUpload.getDocumentTypes({
        productCode: customerSearchValues?.selectedCredit?.creditType || undefined,
      });
      setDocumentTypes(
        data.documentTypeList?.map((documentType) => ({
          value: documentType.code,
          label: documentType.value,
        })) || []
      );
      setIsLoading(false);
    } catch (error) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  }, [customerSearchValues]);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const handleSubmit = (): void => {
    if (!documents?.length) {
      setHasNoDocumentWarning(true);
    } else {
      setIsApplicationCompleted(true);
      setIsCompleted && setIsCompleted(true);
    }
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInputs();
    goToPreviousStep();
    setIsCompleted && setIsCompleted(false);
  };

  const setDocuments = (docs: UploadDocumentFormData[]): void => dispatch({ documents: docs });

  return (
    <Container>
      {isApplicationCompleted ? (
        <Modal
          description={t('uploadDocuments_successMessage')}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      <Title>
        <Trans i18nKey="uploadDocuments_title" />
      </Title>

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="uploadDocuments_type_label" />:
        </InfoLabel>
        {clientInfo?.uploadDocumentFormTypeValue}
      </InfoRow>
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="uploadDocuments_clientName_text" />:
        </InfoLabel>
        {clientInfo?.clientName}
      </InfoRow>
      {clientInfo?.cardNumber ? (
        <InfoRow>
          <InfoLabel>
            <Trans i18nKey="uploadDocuments_cardNumber_text" />:
          </InfoLabel>
          {clientInfo?.cardNumber}
        </InfoRow>
      ) : null}
      {clientInfo?.contractNumber !== '0' ? (
        <InfoRow>
          <InfoLabel>
            <Trans i18nKey="callCenter_internetBanking_rbsAgreement_contractNumber_text" />:
          </InfoLabel>
          {clientInfo?.contractNumber}
        </InfoRow>
      ) : null}

      <Separator />
      <Subtitle>
        <Trans i18nKey="uploadDocuments_uploadDocument_subtitle" />
      </Subtitle>

      <UploadDocument
        defaultExplanation={clientInfo?.cardNumber}
        documents={documents || []}
        documentTypes={documentTypes || []}
        isLoading={isLoading}
        setDocuments={setDocuments}
      />
      {hasNoDocumentWarning && !documents?.length ? (
        <WarningText>
          <Trans i18nKey="general_noUploadedDocument_errorMessage" />
        </WarningText>
      ) : null}

      <FormRow>
        <Button
          color={ButtonColor.Primary}
          onClick={handleSubmit}
          size={ButtonSize.Medium}
          text={t('general_continue_button')}
          type="button"
        />
      </FormRow>
    </Container>
  );
};

export default UploadDocumentList;
